<template>
  <div class="lottery-index">
    <lottery-header class="header" />

    <div class="contents">
      <lottery-total-prize-card :total-prize="totalPrize" />

      <wallet-card-list>
        <lottery-card
          v-for="(item, index) in items"
          :item-data="item"
          :key="index"
        />
      </wallet-card-list>
    </div>
  </div>
</template>

<script>
import WalletCardList from "@/components/molecules/WalletCardList";
import LotteryHeader from "@/components/organisms/lottery/LotteryHeader";
import LotteryCard from "@/components/organisms/lottery/LotteryCard";
import LotteryTotalPrizeCard from "@/components/organisms/lottery/LotteryTotalPrizeCard";
import { getAccountData, getPools } from "@/utils/lotteries";
import Web3 from "web3";
import network from "@/domain/lottery/network";
import token from "@/domain/lottery/token";
import { WALLET_CONNECT, METAMASK } from "@/utils/constants";
import WalletConnectProvider from "@walletconnect/web3-provider";

export default {
  name: "LotteryIndex",
  components: {
    WalletCardList,
    LotteryHeader,
    LotteryCard,
    LotteryTotalPrizeCard,
  },
  computed: {
    totalPrize() {
      return this.$store.getters["lotteries/totalPrizeInUsd"];
    },
    items() {
      return this.$store.state.lotteries.pools;
    },
    web3() {
      return this.$store.state.web3;
    },
    account() {
      return this.$store.state.account;
    },
  },
  methods: {
    getPoolsDataInterval(instance, seconds) {
      let accountAddress = this.account.address;
      this.poolsDataInterval = setInterval(() => {
        getPools(instance, accountAddress).then((result) => {
          this.$store.dispatch("lotteries/updatePools", result);
        });
      }, seconds * 1000);
    },

    async handleAccountChanged(accounts) {
      if (accounts.length == 0) {
        this.$store.dispatch("onLogout");
      }

      const accountData = await getAccountData(
        this.web3.instance,
        token,
        accounts
      );

      if (accountData) {
        this.$store.dispatch("account/updateAccountData", accountData);

        let pools = await getPools(this.web3.instance, this.account.address);
        this.$store.dispatch("lotteries/updatePools", pools);

        this.$store.dispatch("toast/updateToast", {
          type: "success",
          title: "Account Connected",
          message: "Your account is connected!",
        });
      } else {
        this.$store.dispatch("toast/updateToast", {
          type: "error",
          title: "Connect Failed",
          message: "Please check the network!",
        });
      }
    },
    handleChainChanged(chainId) {
      if (!network.chainIds.includes(chainId)) {
        this.$store.dispatch("toast/updateToast", {
          type: "error",
          title: "Connect Failed",
          message: "Please check the network!",
        });
      }
      if (network.chainIds.includes(chainId)) {
        this.$store.dispatch("web3/updateChainId", chainId);
        this.$store.dispatch("toast/updateToast", {
          type: "success",
          title: "Connect Success",
          message: "Successfully switched network.",
        });
      }
    },
    handleDisconnected() {
      this.$store.dispatch("onLogout");
    },
  },
  async mounted() {
    try {
      if (!this.$store.state.isLogin) {
        const provider = new WalletConnectProvider({
          chainId: network.chainIds[0],
          rpc: {
            [network.chainIds[0]]: network.rpcUrls[0],
          },
        });

        if (provider.connected) {
          await provider.disconnect();
          localStorage.removeItem("walletconnect");
        }

        const instance = new Web3(
          new Web3.providers.HttpProvider(network.rpcUrls[0])
        );
        const chainId = await instance.eth.net.getId();

        this.$store.dispatch("web3/update", {
          instance,
          chainId,
          provider: null,
        });
      }

      if (this.$store.state.isLogin) {
        if (sessionStorage.getItem("provider") == WALLET_CONNECT) {
          const provider = new WalletConnectProvider({
            chainId: network.chainIds[0],
            rpc: {
              [network.chainIds[0]]: network.rpcUrls[0],
            },
          });

          if (provider.connected) {
            await provider.disconnect();
          }

          await provider.enable();

          const instance = new Web3(provider);
          const chainId = await instance.eth.net.getId();

          this.$store.dispatch("web3/update", {
            instance,
            chainId,
            provider: WALLET_CONNECT,
          });
        }

        if (sessionStorage.getItem("provider") == METAMASK) {
          if (!window.ethereum) {
            return;
          }

          const instance = new Web3(window.ethereum);
          const chainId = await instance.eth.net.getId();

          this.$store.dispatch("web3/update", {
            instance,
            chainId,
            provider: METAMASK,
          });
        }

        // Subscribe to accounts change
        this.web3.instance.currentProvider.on("accountsChanged", (accounts) => {
          this.handleAccountChanged(accounts);
        });

        // Subscribe to chainId change
        this.web3.instance.currentProvider.on("chainChanged", (chainId) => {
          this.handleChainChanged(chainId);
        });

        // Subscribe to session disconnection
        this.web3.instance.currentProvider.on("disconnect", () => {
          this.handleDisconnected();
        });

        const accountData = await getAccountData(this.web3.instance, token);

        if (accountData) {
          this.$store.dispatch("account/updateAccountData", accountData);

          let pools = await getPools(this.web3.instance, this.account.address);
          this.$store.dispatch("lotteries/updatePools", pools);

          this.$store.dispatch("toast/updateToast", {
            type: "success",
            title: "Account Connected",
            message: "Your account is connected!",
          });
        } else {
          this.$store.dispatch("toast/updateToast", {
            type: "error",
            title: "Connect Failed",
            message: "Please check the network!",
          });
        }
      }

      getPools(this.web3.instance, this.account.address).then((result) => {
        this.$store.dispatch("lotteries/updatePools", result);
      });

      this.getPoolsDataInterval(this.web3.instance, 10);
    } catch (e) {
      console.log(e);
    }
  },
  beforeDestroy() {
    clearInterval(this.poolsDataInterval);

    if (this.$store.state.isLogin) {
      this.web3.instance.currentProvider.removeListener(
        "accountsChanged",
        this.handleAccountChanged
      );
      this.web3.instance.currentProvider.removeListener(
        "chainChanged",
        this.handleChainChanged
      );
      this.web3.instance.currentProvider.removeListener(
        "disconnect",
        this.handleDisconnected
      );
    }
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";

.lottery-index {
  @include media(pc) {
    .header {
      margin-bottom: 56px;
    }
    .contents {
      margin: 0 24px;
      padding-bottom: 56px;
    }
  }
  @include media(sp) {
    .header {
      margin-bottom: 24px;
    }
    .contents {
      margin: 0 16px;
      padding-bottom: 24px;
    }
  }
}
</style>